import { Controller } from "@hotwired/stimulus"
import { createLogger } from "../lib/logger"

// Infinite Scroll Controller
// Intersection Observer로 무한 스크롤 구현
const log = createLogger('InfiniteScroll')
const MAX_SEEN_AD_IDS = 50
const MAX_RECENT_AD_IDS = 6

export default class extends Controller {
  static targets = ["trigger", "items"]
  static values = {
    url: String,
    page: { type: Number, default: 1 },
    hasMore: { type: Boolean, default: true }
  }

  connect() {
    this.feedStateKey = this.buildFeedStateKey()
    this.syncFeedStateFromDOM()

    this.observer = new IntersectionObserver(
      entries => this.handleIntersection(entries),
      {
        root: null,
        rootMargin: "0px 0px 200px 0px", // 하단 200px 전에 미리 로드 (iOS Safari 안정성)
        threshold: 0
      }
    )

    if (this.hasTriggerTarget) {
      this.observer.observe(this.triggerTarget)
    }
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  handleIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting && this.hasMoreValue) {
        this.loadMore()
      }
    })
  }

  async loadMore() {
    if (!this.hasMoreValue) return

    this.hasMoreValue = false // 중복 요청 방지
    this.pageValue += 1

    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("page", this.pageValue)
    this.appendFeedStateParams(url)

    try {
      const response = await fetch(url, {
        headers: {
          Accept: "text/vnd.turbo-stream.html"
        }
      })

      if (!response.ok) throw new Error("Network response was not ok")

      // Content-Type 헤더로 Turbo Stream 응답 판별 (문자열 검사보다 안정적)
      const contentType = response.headers.get('Content-Type') || ''
      if (!contentType.includes('text/vnd.turbo-stream.html')) {
        this.hasMoreValue = false
        this.hideSentinel()
        return
      }

      const html = await response.text()

      // ✅ CLEANUP: Turbo 렌더링 전 기존 센티널 관찰 해제
      if (this.hasTriggerTarget) {
        this.observer.unobserve(this.triggerTarget)
      }

      // Turbo가 센티널 교체 처리
      Turbo.renderStreamMessage(html)

      // ✅ FIX: requestAnimationFrame으로 Stimulus target 업데이트 대기
      // Turbo가 DOM 교체 후 Stimulus MutationObserver가 새 센티널을 감지할 시간 확보
      // 이렇게 하지 않으면 this.triggerTarget이 제거된 요소를 참조하여
      // IntersectionObserver가 영원히 트리거되지 않음 (race condition)
      requestAnimationFrame(() => {
        this.syncFeedStateFromDOM()

        // ✅ RE-OBSERVATION: 새 센티널에 트리거 타겟 존재 여부 확인
        // @pagy.next 있으면 서버가 트리거 포함 센티널 렌더
        // 없으면 트리거 없는 종료 메시지 렌더
        if (this.hasTriggerTarget) {
          this.hasMoreValue = true
          // 새 센티널 요소 재관찰 (이제 올바른 요소 참조)
          this.observer.observe(this.triggerTarget)
          log.debug('Re-observed new sentinel with trigger', { page: this.pageValue })
        } else {
          // 트리거 타겟 없음 = 마지막 페이지
          this.hasMoreValue = false
          log.debug('Last page reached - no trigger to observe', { page: this.pageValue })
        }
      })
    } catch (error) {
      log.error('Failed to load more products', error)
      this.hasMoreValue = false
      this.hideSentinel()
    }
  }

  hideSentinel() {
    const sentinel = document.getElementById("infinite-scroll-sentinel")
    if (sentinel) {
      sentinel.style.display = "none"
    }
  }

  appendFeedStateParams(url) {
    this.syncFeedStateFromDOM()

    const state = this.readFeedState()
    if (!state) return

    if (state.seenAdIds.length > 0) {
      url.searchParams.set("seen_ad_ids", state.seenAdIds.join(","))
    } else {
      url.searchParams.delete("seen_ad_ids")
    }

    if (state.recentAdIds.length > 0) {
      url.searchParams.set("recent_ad_ids", state.recentAdIds.join(","))
    } else {
      url.searchParams.delete("recent_ad_ids")
    }
  }

  syncFeedStateFromDOM() {
    const adIds = this.feedAdElements
      .map(element => parseInt(element.dataset.feedAdId, 10))
      .filter(id => Number.isInteger(id))

    const state = {
      seenAdIds: this.uniqueIds(adIds).slice(-MAX_SEEN_AD_IDS),
      recentAdIds: this.lastUniqueIds(adIds, MAX_RECENT_AD_IDS),
      updatedAt: Date.now()
    }

    this.writeFeedState(state)
  }

  buildFeedStateKey() {
    const feedUrl = new URL(this.urlValue, window.location.origin)
    const sortedParams = new URLSearchParams([...feedUrl.searchParams.entries()].sort())
    const queryString = sortedParams.toString()
    const normalized = queryString ? `${feedUrl.pathname}?${queryString}` : feedUrl.pathname
    return `feed_state:${normalized}`
  }

  readFeedState() {
    try {
      const raw = sessionStorage.getItem(this.feedStateKey)
      if (!raw) return null

      const parsed = JSON.parse(raw)
      return {
        seenAdIds: Array.isArray(parsed.seenAdIds) ? parsed.seenAdIds : [],
        recentAdIds: Array.isArray(parsed.recentAdIds) ? parsed.recentAdIds : []
      }
    } catch {
      return null
    }
  }

  writeFeedState(state) {
    try {
      sessionStorage.setItem(this.feedStateKey, JSON.stringify(state))
    } catch {
      log.debug("Skipping feed state persistence")
    }
  }

  uniqueIds(ids) {
    return [...new Set(ids)]
  }

  lastUniqueIds(ids, limit) {
    const seen = new Set()
    const result = []

    for (let index = ids.length - 1; index >= 0; index -= 1) {
      const id = ids[index]
      if (seen.has(id)) continue

      seen.add(id)
      result.unshift(id)

      if (result.length >= limit) break
    }

    return result
  }

  get feedAdElements() {
    const scope = this.hasItemsTarget ? this.itemsTarget : this.element
    return Array.from(scope.querySelectorAll("[data-feed-ad-id]"))
  }
}
